import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { comprasAPI, productosAPI, gastosAPI } from '../services/api';
import { getImageUrl } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Compras = () => {
  const { proveedores, formatCurrency, config } = useApp();
  const { modoEspectador } = useAuth();
  const [compras, setCompras] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetalle, setShowDetalle] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('compras'); // 'compras' | 'cuentas-por-pagar'

  // Filtros
  const [filtros, setFiltros] = useState({
    buscar: '',
    proveedor: '',
    numero_factura: '',
    estado: '',
    estado_pago: '',
    fecha_desde: '',
    fecha_hasta: ''
  });

  const [formData, setFormData] = useState({
    proveedor_id: '',
    fecha_compra: new Date().toISOString().split('T')[0],
    numero_factura: '',
    notas: '',
    condicion_pago: 'contado',
    dias_credito: 30,
    detalles: []
  });
  const [itemTemp, setItemTemp] = useState({ producto_id: '', cantidad: 1, precio_unitario: '', iva_porcentaje: 15 });

  // Facturas (comprobantes del proveedor)
  const [facturas, setFacturas] = useState([]);
  const [uploadingFactura, setUploadingFactura] = useState(false);
  const facturaInputRef = useRef(null);

  // Adjuntos en modal de creación (hasta 6 archivos)
  const [adjuntos, setAdjuntos] = useState([]);
  const adjuntosInputRef = useRef(null);

  // Pago
  const [showPagoModal, setShowPagoModal] = useState(null);
  const [pagoForm, setPagoForm] = useState({ metodo_pago: 'caja_chica', numero_documento: '' });
  const [savingPago, setSavingPago] = useState(false);

  // Cuentas por pagar
  const [cuentasPorPagar, setCuentasPorPagar] = useState([]);
  const [loadingCxP, setLoadingCxP] = useState(false);
  const [cxpTotales, setCxpTotales] = useState({ total_por_pagar: 0, total_vencido: 0, total_por_vencer: 0 });

  // Caja chica info
  const [cajaChica, setCajaChica] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const params = { limit: 20, page };
      if (filtros.buscar) params.buscar = filtros.buscar;
      if (filtros.proveedor) params.proveedor = filtros.proveedor;
      if (filtros.estado) params.estado = filtros.estado;
      if (filtros.estado_pago) params.estado_pago = filtros.estado_pago;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;

      const comprasRes = await comprasAPI.getAll(params);
      if (comprasRes.data.success) {
        setCompras(comprasRes.data.data);
        setTotal(comprasRes.data.total || comprasRes.data.data.length);
        setTotalPages(comprasRes.data.totalPages || 1);
      }

      // Products for dropdown only if not loaded yet
      if (productos.length === 0) {
        const prodRes = await productosAPI.getAll({ activo: true });
        if (prodRes.data.success) setProductos(prodRes.data.data);
      }
    }
    catch (e) { toast.error('Error'); }
    finally { setLoading(false); }
  }, [filtros, page]);

  const cargarCuentasPorPagar = useCallback(async () => {
    setLoadingCxP(true);
    try {
      const res = await comprasAPI.getCuentasPorPagar();
      if (res.data.success) {
        setCuentasPorPagar(res.data.data || []);
        const resumen = res.data.resumen || {};
        // Calculate total_por_vencer from data
        let totalPorVencer = 0;
        (res.data.data || []).forEach(c => {
          if (c.estado_vencimiento === 'por_vencer') {
            totalPorVencer += parseFloat(c.total) - parseFloat(c.monto_pagado || 0);
          }
        });
        setCxpTotales({
          total_por_pagar: resumen.total_por_pagar || 0,
          total_vencido: resumen.total_vencido || 0,
          total_por_vencer: totalPorVencer
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoadingCxP(false); }
  }, []);

  const cargarCajaChica = useCallback(async () => {
    try {
      const res = await gastosAPI.getCajaChica();
      if (res.data.success) setCajaChica(res.data.data);
    } catch {}
  }, []);

  useEffect(() => { load(); cargarCajaChica(); }, [load, cargarCajaChica]);

  // FIX: Reset pagination when filters change to avoid showing empty pages
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setPage(1); // FIX: Reset page when any filter changes
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const limpiarFiltros = () => {
    setPage(1); // FIX: Reset page when clearing all filters
    setFiltros({ buscar: '', proveedor: '', numero_factura: '', estado: '', estado_pago: '', fecha_desde: '', fecha_hasta: '' });
  };

  const openModal = () => {
    setFormData({
      proveedor_id: '',
      fecha_compra: new Date().toISOString().split('T')[0],
      numero_factura: '',
      notas: '',
      condicion_pago: 'contado',
      dias_credito: 30,
      detalles: []
    });
    setItemTemp({ producto_id: '', cantidad: 1, precio_unitario: '', iva_porcentaje: config.iva_defecto || 15 });
    setAdjuntos([]);
    setShowModal(true);
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemTemp(prev => ({ ...prev, [name]: value }));
    if (name === 'producto_id' && value) {
      const prod = productos.find(p => p.id === parseInt(value));
      if (prod) setItemTemp(prev => ({ ...prev, precio_unitario: prod.precio_compra || '', iva_porcentaje: prod.iva_porcentaje || 15 }));
    }
  };

  // FIX: Validate item data with null checks before adding to purchase details
  const agregarItem = () => {
    if (!itemTemp.producto_id || !itemTemp.cantidad || !itemTemp.precio_unitario) {
      toast.error('Complete los campos del producto');
      return;
    }
    // FIX: Validate precio > 0 to prevent $0.00 line items
    const precioUnit = parseFloat(itemTemp.precio_unitario) || 0;
    if (precioUnit <= 0) {
      toast.error('El precio unitario debe ser mayor a cero');
      return;
    }
    const prod = productos.find(p => p.id === parseInt(itemTemp.producto_id));
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, {
        ...itemTemp,
        producto_id: parseInt(itemTemp.producto_id),
        cantidad: Math.max(1, parseInt(itemTemp.cantidad) || 1),
        precio_unitario: precioUnit,
        iva_porcentaje: parseFloat(itemTemp.iva_porcentaje) || 0,
        producto_nombre: prod?.nombre || 'Sin nombre'
      }]
    }));
    setItemTemp({ producto_id: '', cantidad: 1, precio_unitario: '', iva_porcentaje: config.iva_defecto || 15 });
  };

  const quitarItem = (idx) => setFormData(prev => ({ ...prev, detalles: prev.detalles.filter((_, i) => i !== idx) }));

  // FIX: Null-safe calculation with parseFloat fallback for all values
  const calcularTotales = () => {
    let subtotal = 0, iva = 0;
    formData.detalles.forEach(d => {
      const cantidad = parseInt(d.cantidad) || 0;
      const precioUnit = parseFloat(d.precio_unitario) || 0;
      const ivaPct = parseFloat(d.iva_porcentaje) || 0;
      const s = cantidad * precioUnit;
      subtotal += s;
      iva += s * (ivaPct / 100);
    });
    return { subtotal, iva, total: Math.max(0, subtotal + iva) };
  };

  // ─── Adjuntos en formulario de nueva compra ───
  const handleAdjuntosChange = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const validFiles = files.filter(f => allowedTypes.includes(f.type));
    const invalidCount = files.length - validFiles.length;
    if (invalidCount > 0) toast.warning(`${invalidCount} archivo(s) ignorados (solo PDF e imágenes)`);

    setAdjuntos(prev => {
      const combined = [...prev, ...validFiles];
      if (combined.length > 6) {
        toast.warning('Máximo 6 adjuntos permitidos');
        return combined.slice(0, 6);
      }
      return combined;
    });
    if (adjuntosInputRef.current) adjuntosInputRef.current.value = '';
  };

  const quitarAdjunto = (idx) => setAdjuntos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.detalles.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }
    if (adjuntos.length === 0) {
      toast.error('Debe adjuntar al menos un comprobante (foto o PDF)');
      return;
    }
    try {
      const res = await comprasAPI.create({
        ...formData,
        proveedor_id: formData.proveedor_id || null,
        detalles: formData.detalles.map(d => ({
          producto_id: d.producto_id,
          cantidad: d.cantidad,
          precio_unitario: d.precio_unitario,
          iva_porcentaje: d.iva_porcentaje
        }))
      });

      const compraCreada = res.data?.data;

      // Si hay adjuntos, subirlos inmediatamente
      if (adjuntos.length > 0 && compraCreada?.id) {
        try {
          await comprasAPI.uploadFactura(compraCreada.id, adjuntos);
          toast.success(`Compra registrada con ${adjuntos.length} comprobante(s) adjunto(s)`);
        } catch (uploadErr) {
          toast.warning('Compra creada pero hubo un error al subir comprobantes. Puede adjuntarlos desde el detalle.');
          console.error('Error subiendo adjuntos:', uploadErr);
        }
      } else {
        toast.success('Compra registrada correctamente');
      }

      setShowModal(false);
      load();
    }
    catch (e) { toast.error(e.response?.data?.error || 'Error al registrar compra'); }
  };

  const confirmar = async (id) => {
    try {
      await comprasAPI.confirmar(id);
      toast.success('Compra confirmada - Stock actualizado');
      load();
      if (showDetalle) {
        const res = await comprasAPI.getById(id);
        if (res.data.success) setShowDetalle(res.data.data);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Error al confirmar'); }
  };

  const cancelar = async (id) => {
    const motivo = window.prompt('Ingrese el motivo de cancelación:');
    if (!motivo) return;
    try {
      await comprasAPI.cancelar(id, { motivo });
      toast.success('Compra cancelada');
      load();
    } catch (e) { toast.error('Error al cancelar'); }
  };

  const verDetalle = async (id) => {
    try {
      const [compraRes, facturasRes] = await Promise.all([
        comprasAPI.getById(id),
        comprasAPI.getFacturas(id)
      ]);
      if (compraRes.data.success) setShowDetalle(compraRes.data.data);
      if (facturasRes.data.success) setFacturas(facturasRes.data.data || []);
      else setFacturas([]);
    } catch (e) { toast.error('Error al cargar detalle'); }
  };

  // ─── Facturas (comprobantes proveedor) ───
  const handleUploadFactura = async (files) => {
    if (!showDetalle) return;
    setUploadingFactura(true);
    try {
      const res = await comprasAPI.uploadFactura(showDetalle.id, Array.from(files));
      if (res.data.success) {
        toast.success('Factura subida');
        const fRes = await comprasAPI.getFacturas(showDetalle.id);
        if (fRes.data.success) setFacturas(fRes.data.data || []);
        // Refresh compra detail
        const cRes = await comprasAPI.getById(showDetalle.id);
        if (cRes.data.success) setShowDetalle(cRes.data.data);
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al subir factura');
    } finally { setUploadingFactura(false); }
  };

  const handleDeleteFactura = async (comprobanteId) => {
    if (!showDetalle) return;
    if (!window.confirm('¿Eliminar esta factura?')) return;
    try {
      await comprasAPI.deleteFactura(showDetalle.id, comprobanteId);
      toast.success('Factura eliminada');
      const fRes = await comprasAPI.getFacturas(showDetalle.id);
      if (fRes.data.success) setFacturas(fRes.data.data || []);
      const cRes = await comprasAPI.getById(showDetalle.id);
      if (cRes.data.success) setShowDetalle(cRes.data.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al eliminar');
    }
  };

  // ─── Pago ───
  const handleRegistrarPago = async () => {
    if (!showPagoModal) return;
    // FIX: Validate total > 0 to prevent $0.00 payment bug
    const totalPago = parseFloat(showPagoModal.total) || 0;
    if (totalPago <= 0) {
      toast.error('El monto a pagar debe ser mayor a cero');
      return;
    }
    setSavingPago(true);
    try {
      const res = await comprasAPI.registrarPago(showPagoModal.id, pagoForm);
      if (res.data.success) {
        toast.success('Pago registrado exitosamente');
        setShowPagoModal(null);
        load();
        if (showDetalle) {
          const cRes = await comprasAPI.getById(showDetalle.id);
          if (cRes.data.success) setShowDetalle(cRes.data.data);
        }
        cargarCuentasPorPagar();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al registrar pago');
    } finally { setSavingPago(false); }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      pendiente: <span className="badge bg-warning text-dark">Pendiente</span>,
      confirmada: <span className="badge bg-success">Confirmada</span>,
      cancelada: <span className="badge bg-danger">Cancelada</span>
    };
    return estados[estado] || <span className="badge bg-secondary">{estado}</span>;
  };

  const getPagoBadge = (estado_pago) => {
    const map = {
      pendiente: <span className="badge bg-danger bg-opacity-10 text-danger">Sin pagar</span>,
      parcial: <span className="badge bg-warning bg-opacity-10 text-warning">Parcial</span>,
      pagado: <span className="badge bg-success bg-opacity-10 text-success">Pagado</span>
    };
    return map[estado_pago] || null;
  };

  const totales = calcularTotales();
  const hayFiltros = filtros.buscar || filtros.proveedor || filtros.numero_factura || filtros.estado || filtros.estado_pago || filtros.fecha_desde || filtros.fecha_hasta;

  return (
    <div>
      {/* TABS */}
      <ul className="nav nav-pills mb-4">
        <li className="nav-item">
          {/* FIX: Reset pagination when switching tabs to avoid stale page state */}
          <button className={`nav-link ${tab === 'compras' ? 'active' : ''}`} onClick={() => { setTab('compras'); setPage(1); }}>
            <i className="bi bi-cart me-1"></i>Compras
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'cuentas-por-pagar' ? 'active' : ''}`} onClick={() => { setTab('cuentas-por-pagar'); setPage(1); cargarCuentasPorPagar(); }}>
            <i className="bi bi-calendar-check me-1"></i>Cuentas por Pagar
            {cuentasPorPagar.length > 0 && <span className="badge bg-danger ms-1">{cuentasPorPagar.length}</span>}
          </button>
        </li>
      </ul>

      {/* ================================================================ */}
      {/* TAB: COMPRAS                                                     */}
      {/* ================================================================ */}
      {tab === 'compras' && (
        <>
          {/* Filtros + Botón Nueva */}
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <div className="input-group input-group-sm" style={{maxWidth:280}}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input type="text" className="form-control" name="buscar" placeholder="Buscar por N° compra, factura o proveedor..."
                value={filtros.buscar} onChange={handleFiltroChange} />
            </div>
            <select className="form-select form-select-sm" style={{maxWidth:160}} name="proveedor" value={filtros.proveedor} onChange={handleFiltroChange}>
              <option value="">Proveedor: Todos</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{maxWidth:140}} name="estado" value={filtros.estado} onChange={handleFiltroChange}>
              <option value="">Estado: Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <select className="form-select form-select-sm" style={{maxWidth:130}} name="estado_pago" value={filtros.estado_pago} onChange={handleFiltroChange}>
              <option value="">Pago: Todos</option>
              <option value="pendiente">Sin pagar</option>
              <option value="pagado">Pagado</option>
            </select>
            {hayFiltros && (
              <button className="btn btn-sm btn-outline-secondary" onClick={limpiarFiltros}><i className="bi bi-x-lg"></i></button>
            )}
            <button className="btn btn-sm btn-primary ms-auto" onClick={openModal} disabled={modoEspectador}>
              <i className="bi bi-plus-lg me-1"></i>Nueva Compra
            </button>
          </div>

          {/* Lista de Compras — Cards en móvil, Tabla en desktop */}
          <div className="card">
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : compras.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-cart fs-1 text-muted d-block mb-2"></i>
                  <h6 className="text-muted">No hay compras{hayFiltros ? ' con los filtros seleccionados' : ''}</h6>
                  {hayFiltros && <button className="btn btn-outline-primary btn-sm mt-2" onClick={limpiarFiltros}>Limpiar Filtros</button>}
                </div>
              ) : (
                <>
                  {/* Vista móvil: Cards */}
                  <div className="d-md-none">
                    {compras.map(c => (
                      <div key={c.id} className={`border-bottom p-3 ${c.alerta_vencimiento === 'vencida' ? 'bg-danger bg-opacity-10' : c.alerta_vencimiento === 'por_vencer' ? 'bg-warning bg-opacity-10' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <code className="fw-bold" style={{fontSize:'0.8rem'}}>{c.numero_compra}</code>
                            <div className="text-muted" style={{fontSize:'0.75rem'}}>{c.fecha_compra ? new Date(c.fecha_compra).toLocaleDateString('es-EC') : '—'}</div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold text-primary">{formatCurrency(c.total)}</div>
                            <div className="d-flex gap-1 justify-content-end mt-1">
                              {getEstadoBadge(c.estado)}
                              {getPagoBadge(c.estado_pago)}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small">
                            <i className="bi bi-building me-1"></i>{c.proveedor_nombre || <span className="text-muted">Sin proveedor</span>}
                            {c.tiene_factura ? <i className="bi bi-paperclip text-success ms-2" title="Con factura"></i> : <i className="bi bi-paperclip text-danger ms-2" title="Sin factura"></i>}
                            {c.condicion_pago === 'credito' && c.estado_pago !== 'pagado' && <span className="text-muted ms-2">{c.dias_credito}d crédito</span>}
                          </div>
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-info btn-sm" onClick={() => verDetalle(c.id)}><i className="bi bi-eye"></i></button>
                            {c.estado === 'pendiente' && !modoEspectador && (
                              <>
                                <button className="btn btn-outline-success btn-sm" onClick={() => confirmar(c.id)}><i className="bi bi-check-lg"></i></button>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => cancelar(c.id)}><i className="bi bi-x-lg"></i></button>
                              </>
                            )}
                            {c.estado === 'confirmada' && c.estado_pago !== 'pagado' && !modoEspectador && (
                              <button className="btn btn-outline-warning btn-sm" onClick={() => { setShowPagoModal(c); setPagoForm({ metodo_pago: 'caja_chica', numero_documento: '' }); cargarCajaChica(); }}><i className="bi bi-cash"></i></button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Vista desktop: Tabla */}
                  <div className="d-none d-md-block table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Nº Compra</th>
                          <th>Fecha</th>
                          <th>Proveedor</th>
                          <th className="text-end">Total</th>
                          <th className="text-center">Estado</th>
                          <th className="text-center">Pago</th>
                          <th className="text-center">Factura</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compras.map(c => (
                          <tr key={c.id} className={c.alerta_vencimiento === 'vencida' ? 'table-danger' : c.alerta_vencimiento === 'por_vencer' ? 'table-warning' : ''}>
                            <td><code>{c.numero_compra}</code></td>
                            <td>{c.fecha_compra ? new Date(c.fecha_compra).toLocaleDateString('es-EC') : '—'}</td>
                            <td>{c.proveedor_nombre || <span className="text-muted">Sin proveedor</span>}</td>
                            <td className="text-end fw-bold">{formatCurrency(c.total)}</td>
                            <td className="text-center">{getEstadoBadge(c.estado)}</td>
                            <td className="text-center">
                              {getPagoBadge(c.estado_pago)}
                              {c.condicion_pago === 'credito' && c.estado_pago !== 'pagado' && (
                                <div className="small text-muted">{c.dias_credito}d crédito</div>
                              )}
                            </td>
                            <td className="text-center">
                              {c.tiene_factura ? <i className="bi bi-file-earmark-check text-success"></i> : <i className="bi bi-file-earmark-x text-danger"></i>}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-info" onClick={() => verDetalle(c.id)} title="Ver detalle"><i className="bi bi-eye"></i></button>
                                {c.estado === 'pendiente' && !modoEspectador && (
                                  <>
                                    <button className="btn btn-outline-success" onClick={() => confirmar(c.id)} title="Confirmar recepción"><i className="bi bi-check-lg"></i></button>
                                    <button className="btn btn-outline-danger" onClick={() => cancelar(c.id)} title="Cancelar"><i className="bi bi-x-lg"></i></button>
                                  </>
                                )}
                                {c.estado === 'confirmada' && c.estado_pago !== 'pagado' && !modoEspectador && (
                                  <button className="btn btn-outline-warning" onClick={() => { setShowPagoModal(c); setPagoForm({ metodo_pago: 'caja_chica', numero_documento: '' }); cargarCajaChica(); }} title="Registrar pago">
                                    <i className="bi bi-cash"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            {compras.length > 0 && (
              <div className="card-footer d-flex justify-content-between align-items-center">
                <span className="text-muted">Mostrando {compras.length} de {total} compra(s)</span>
                {totalPages > 1 && (
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      {/* FIX: Guard pagination boundaries to prevent out-of-range page values */}
                      <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Anterior</button>
                      </li>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pg;
                        if (totalPages <= 5) pg = i + 1;
                        else if (page <= 3) pg = i + 1;
                        else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                        else pg = page - 2 + i;
                        return (
                          <li key={pg} className={`page-item ${page === pg ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setPage(pg)}>{pg}</button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Siguiente</button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* TAB: CUENTAS POR PAGAR                                           */}
      {/* ================================================================ */}
      {tab === 'cuentas-por-pagar' && (
        <>
          {/* Resumen cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="stat-card">
                <p className="stat-label mb-1">Total por Pagar</p>
                <h3 className="stat-value mb-0 text-danger">{formatCurrency(cxpTotales.total_por_pagar || 0)}</h3>
                <small className="text-muted">{cuentasPorPagar.length} compra(s) pendiente(s)</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card">
                <p className="stat-label mb-1">Vencido</p>
                <h3 className="stat-value mb-0 text-danger">{formatCurrency(cxpTotales.total_vencido || 0)}</h3>
                <small className="text-muted">requiere pago inmediato</small>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card">
                <p className="stat-label mb-1">Por Vencer (7 días)</p>
                <h3 className="stat-value mb-0 text-warning">{formatCurrency(cxpTotales.total_por_vencer || 0)}</h3>
                <small className="text-muted">próximo a vencer</small>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h6 className="mb-0"><i className="bi bi-calendar-check me-2"></i>Compras a Crédito Pendientes de Pago</h6>
            </div>
            <div className="card-body p-0">
              {loadingCxP ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : cuentasPorPagar.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-check-circle fs-1 d-block mb-2 text-success"></i>
                  <h5>Sin cuentas pendientes</h5>
                  <p>Todas las compras a crédito están al día</p>
                </div>
              ) : (
                <>
                <div className="d-md-none">
                  {cuentasPorPagar.map(c => (
                    <div key={c.id} className={`border-bottom p-3 ${c.estado_vencimiento === 'vencida' ? 'bg-danger bg-opacity-10' : c.estado_vencimiento === 'por_vencer' ? 'bg-warning bg-opacity-10' : ''}`}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <code style={{fontSize:'0.8rem'}}>{c.numero_compra}</code>
                          <div className="small">{c.proveedor_nombre}</div>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold">{formatCurrency(c.total)}</div>
                          {c.estado_vencimiento === 'vencida' && <span className="badge bg-danger">Vencida</span>}
                          {c.estado_vencimiento === 'por_vencer' && <span className="badge bg-warning text-dark">Por vencer</span>}
                          {c.estado_vencimiento === 'vigente' && <span className="badge bg-success">Vigente</span>}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="small text-muted">
                          <i className="bi bi-calendar me-1"></i>Vence: {c.fecha_vencimiento_pago ? new Date(c.fecha_vencimiento_pago).toLocaleDateString('es-EC') : '—'}
                          <span className={`ms-2 fw-bold ${c.dias_restantes < 0 ? 'text-danger' : 'text-success'}`}>
                            {c.dias_restantes < 0 ? `${Math.abs(c.dias_restantes)}d vencido` : `${c.dias_restantes}d`}
                          </span>
                        </div>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-info btn-sm" onClick={() => verDetalle(c.id)}><i className="bi bi-eye"></i></button>
                          {!modoEspectador && (
                            <button className="btn btn-outline-warning btn-sm" onClick={() => { setShowPagoModal(c); setPagoForm({ metodo_pago: 'caja_chica', numero_documento: '' }); cargarCajaChica(); }}>
                              <i className="bi bi-cash me-1"></i>Pagar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Vista desktop CxP - tabla */}
                <div className="d-none d-md-block table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Nº Compra</th>
                        <th>Proveedor</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Crédito</th>
                        <th className="text-center">Vencimiento</th>
                        <th className="text-center">Días Rest.</th>
                        <th className="text-center">Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuentasPorPagar.map(c => (
                        <tr key={c.id} className={c.estado_vencimiento === 'vencida' ? 'table-danger' : c.estado_vencimiento === 'por_vencer' ? 'table-warning' : ''}>
                          <td><code>{c.numero_compra}</code></td>
                          <td>{c.proveedor_nombre}</td>
                          <td className="text-end fw-bold">{formatCurrency(c.total)}</td>
                          <td className="text-center">{c.dias_credito}d</td>
                          <td className="text-center">{c.fecha_vencimiento_pago ? new Date(c.fecha_vencimiento_pago).toLocaleDateString('es-EC') : '—'}</td>
                          <td className="text-center">
                            <span className={`fw-bold ${c.dias_restantes < 0 ? 'text-danger' : c.dias_restantes <= 7 ? 'text-warning' : 'text-success'}`}>
                              {c.dias_restantes < 0 ? `${Math.abs(c.dias_restantes)}d vencido` : `${c.dias_restantes}d`}
                            </span>
                          </td>
                          <td className="text-center">
                            {c.estado_vencimiento === 'vencida' && <span className="badge bg-danger">Vencida</span>}
                            {c.estado_vencimiento === 'por_vencer' && <span className="badge bg-warning text-dark">Por vencer</span>}
                            {c.estado_vencimiento === 'vigente' && <span className="badge bg-success">Vigente</span>}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-info" onClick={() => verDetalle(c.id)}><i className="bi bi-eye"></i></button>
                              {!modoEspectador && (
                                <button className="btn btn-outline-warning" onClick={() => { setShowPagoModal(c); setPagoForm({ metodo_pago: 'caja_chica', numero_documento: '' }); cargarCajaChica(); }}>
                                  <i className="bi bi-cash me-1"></i>Pagar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* MODAL: Nueva Compra                                              */}
      {/* ================================================================ */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-fullscreen-md-down modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-cart-plus me-2"></i>Nueva Compra</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                      <label className="form-label small">Proveedor</label>
                      <select className="form-select form-select-sm" value={formData.proveedor_id} onChange={e => setFormData({...formData, proveedor_id: e.target.value})}>
                        <option value="">Seleccionar...</option>
                        {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label small">Fecha</label>
                      <input type="date" className="form-control form-control-sm" value={formData.fecha_compra} onChange={e => setFormData({...formData, fecha_compra: e.target.value})} />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label small">Condición de Pago</label>
                      <select className="form-select form-select-sm" value={formData.condicion_pago} onChange={e => setFormData({...formData, condicion_pago: e.target.value})}>
                        <option value="contado">Contado</option>
                        <option value="credito">Crédito</option>
                      </select>
                    </div>
                    {formData.condicion_pago === 'credito' && (
                      <div className="col-6 col-md-3">
                        <label className="form-label small">Días Crédito</label>
                        <select className="form-select form-select-sm" value={formData.dias_credito} onChange={e => setFormData({...formData, dias_credito: parseInt(e.target.value)})}>
                          <option value={15}>15 días</option>
                          <option value={30}>30 días</option>
                          <option value={45}>45 días</option>
                          <option value={60}>60 días</option>
                          <option value={90}>90 días</option>
                        </select>
                      </div>
                    )}
                    <div className="col-6 col-md-3">
                      <label className="form-label small">Nº Factura</label>
                      <input type="text" className="form-control form-control-sm" placeholder="001-001-000012345" value={formData.numero_factura} onChange={e => setFormData({...formData, numero_factura: e.target.value})} />
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div className="card-header bg-light py-2"><h6 className="mb-0 small"><i className="bi bi-box me-2"></i>Agregar Productos</h6></div>
                    <div className="card-body py-2">
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-4">
                          <label className="form-label small mb-1">Producto</label>
                          <select className="form-select form-select-sm" name="producto_id" value={itemTemp.producto_id} onChange={handleItemChange}>
                            <option value="">Seleccionar...</option>
                            {productos.map(p => <option key={p.id} value={p.id}>{p.codigo} - {p.nombre}</option>)}
                          </select>
                        </div>
                        <div className="col-4 col-md-2">
                          <label className="form-label small mb-1">Cant.</label>
                          <input type="number" className="form-control form-control-sm" name="cantidad" value={itemTemp.cantidad} onChange={handleItemChange} min="1" />
                        </div>
                        <div className="col-4 col-md-2">
                          <label className="form-label small mb-1">P.Unit.</label>
                          <input type="number" step="0.01" className="form-control form-control-sm" name="precio_unitario" value={itemTemp.precio_unitario} onChange={handleItemChange} />
                        </div>
                        <div className="col-4 col-md-2">
                          <label className="form-label small mb-1">IVA %</label>
                          <input type="number" step="0.01" className="form-control form-control-sm" name="iva_porcentaje" value={itemTemp.iva_porcentaje} onChange={handleItemChange} />
                        </div>
                        <div className="col-12 col-md-2">
                          <button type="button" className="btn btn-success btn-sm w-100" onClick={agregarItem}><i className="bi bi-plus-lg me-1"></i>Agregar</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {formData.detalles.length > 0 && (
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered">
                        <thead className="table-light">
                          <tr><th>Producto</th><th className="text-center">Cant.</th><th className="text-end">P.Unit.</th><th className="text-center">IVA%</th><th className="text-end">Subtotal</th><th className="text-end">Total</th><th></th></tr>
                        </thead>
                        <tbody>
                          {formData.detalles.map((d, i) => {
                            const sub = d.cantidad * d.precio_unitario;
                            return (
                              <tr key={i}>
                                <td>{d.producto_nombre}</td>
                                <td className="text-center">{d.cantidad}</td>
                                <td className="text-end">{formatCurrency(d.precio_unitario)}</td>
                                <td className="text-center">{d.iva_porcentaje}%</td>
                                <td className="text-end">{formatCurrency(sub)}</td>
                                <td className="text-end fw-bold">{formatCurrency(sub * (1 + d.iva_porcentaje / 100))}</td>
                                <td className="text-center"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarItem(i)}><i className="bi bi-trash"></i></button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="table-light">
                          <tr><td colSpan="5" className="text-end fw-bold">TOTAL:</td><td className="text-end fw-bold text-primary fs-5">{formatCurrency(totales.total)}</td><td></td></tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* ─── Adjuntos / Comprobantes ─── */}
                  <div className="card mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0"><i className="bi bi-paperclip me-2"></i>Comprobantes / Facturas</h6>
                      <span className="badge bg-secondary">{adjuntos.length} / 6</span>
                    </div>
                    <div className="card-body">
                      <input
                        ref={adjuntosInputRef}
                        type="file"
                        className="d-none"
                        multiple
                        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                        onChange={handleAdjuntosChange}
                      />

                      {adjuntos.length === 0 ? (
                        <div
                          className="border-2 border-dashed rounded p-4 text-center"
                          style={{ borderStyle: 'dashed', cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                          onClick={() => adjuntosInputRef.current?.click()}
                        >
                          <i className="bi bi-cloud-arrow-up fs-2 text-muted d-block mb-2"></i>
                          <p className="mb-1 text-muted">Haga clic para adjuntar facturas o comprobantes</p>
                          <small className="text-muted">PDF, JPG, PNG, GIF, WEBP — Máx. 6 archivos, 10MB c/u</small>
                        </div>
                      ) : (
                        <>
                          <div className="row g-2 mb-2">
                            {adjuntos.map((file, idx) => (
                              <div key={idx} className="col-12 col-md-6">
                                <div className="d-flex align-items-center gap-2 border rounded py-2 px-3">
                                  {file.type === 'application/pdf' ? (
                                    <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded" style={{ width: 40, height: 40, minWidth: 40 }}>
                                      <i className="bi bi-file-pdf text-danger fs-5"></i>
                                    </div>
                                  ) : (
                                    <img
                                      src={URL.createObjectURL(file)}
                                      alt={file.name}
                                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, minWidth: 40 }}
                                    />
                                  )}
                                  <div className="flex-grow-1 overflow-hidden">
                                    <div className="small fw-semibold text-truncate">{file.name}</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{(file.size / 1024).toFixed(0)} KB</div>
                                  </div>
                                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarAdjunto(idx)} title="Quitar">
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          {adjuntos.length < 6 && (
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => adjuntosInputRef.current?.click()}>
                              <i className="bi bi-plus-lg me-1"></i>Agregar más archivos
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notas</label>
                    <textarea className="form-control" rows="2" value={formData.notas} onChange={e => setFormData({...formData, notas: e.target.value})} placeholder="Observaciones adicionales..."></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={formData.detalles.length === 0 || adjuntos.length === 0}>
                    <i className="bi bi-check-lg me-2"></i>Registrar Compra
                    {adjuntos.length === 0 && <small className="ms-1 opacity-75">(requiere comprobante)</small>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL: Detalle Compra + Facturas                                 */}
      {/* ================================================================ */}
      {showDetalle && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-fullscreen-md-down modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-receipt me-2"></i>Compra: {showDetalle.numero_compra}</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetalle(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p className="mb-1"><strong>Proveedor:</strong> {showDetalle.proveedor_nombre || 'No especificado'}</p>
                    <p className="mb-1"><strong>Fecha:</strong> {showDetalle.fecha_compra ? new Date(showDetalle.fecha_compra).toLocaleDateString('es-EC') : '—'}</p>
                    <p className="mb-1"><strong>Nº Factura:</strong> {showDetalle.numero_factura || '-'}</p>
                    <p className="mb-1"><strong>Condición:</strong> {showDetalle.condicion_pago === 'credito' ? `Crédito ${showDetalle.dias_credito} días` : 'Contado'}</p>
                  </div>
                  <div className="col-md-6 text-md-end">
                    <p className="mb-1"><strong>Estado:</strong> {getEstadoBadge(showDetalle.estado)}</p>
                    <p className="mb-1"><strong>Pago:</strong> {getPagoBadge(showDetalle.estado_pago)}</p>
                    {showDetalle.fecha_recepcion && <p className="mb-1"><strong>Recibido:</strong> {new Date(showDetalle.fecha_recepcion).toLocaleDateString('es-EC')}</p>}
                    {showDetalle.fecha_vencimiento_pago && showDetalle.estado_pago !== 'pagado' && (
                      <p className="mb-1"><strong>Vence:</strong> <span className="text-danger">{showDetalle.fecha_vencimiento_pago ? new Date(showDetalle.fecha_vencimiento_pago).toLocaleDateString('es-EC') : '—'}</span></p>
                    )}
                    {showDetalle.gasto_codigo && (
                      <p className="mb-1"><strong>Gasto:</strong> <code>{showDetalle.gasto_codigo}</code> <span className={`badge ${showDetalle.gasto_estado === 'pagado' ? 'bg-success' : 'bg-warning text-dark'}`}>{showDetalle.gasto_estado}</span></p>
                    )}
                  </div>
                </div>

                {/* Tabla de productos */}
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead className="table-light"><tr><th>Código</th><th>Producto</th><th className="text-center">Cant.</th><th className="text-end">P.Unit.</th><th className="text-end">Total</th></tr></thead>
                    <tbody>
                      {showDetalle.detalles?.map((d, i) => (
                        <tr key={i}><td><code>{d.producto_codigo}</code></td><td>{d.producto_nombre}</td><td className="text-center">{d.cantidad}</td><td className="text-end">{formatCurrency(d.precio_unitario)}</td><td className="text-end">{formatCurrency(d.total)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr><td colSpan="4" className="text-end"><strong>Subtotal:</strong></td><td className="text-end">{formatCurrency(showDetalle.subtotal)}</td></tr>
                      <tr><td colSpan="4" className="text-end"><strong>IVA:</strong></td><td className="text-end">{formatCurrency(showDetalle.iva_total)}</td></tr>
                      <tr><td colSpan="4" className="text-end"><strong>TOTAL:</strong></td><td className="text-end fw-bold text-primary fs-5">{formatCurrency(showDetalle.total)}</td></tr>
                    </tfoot>
                  </table>
                </div>

                {showDetalle.notas && <div className="alert alert-secondary py-2 small mt-2"><strong>Notas:</strong> {showDetalle.notas}</div>}

                {/* ─── Sección Facturas del Proveedor ─── */}
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <i className="bi bi-file-earmark-text me-2"></i>Factura del Proveedor
                    <span className="badge bg-danger ms-2">Obligatorio para confirmar</span>
                  </h6>
                  {showDetalle.estado === 'pendiente' && !modoEspectador && (
                    <>
                      <input
                        ref={facturaInputRef}
                        type="file"
                        className="d-none"
                        multiple
                        accept="image/jpeg,image/png,application/pdf"
                        onChange={(e) => { if (e.target.files.length > 0) handleUploadFactura(e.target.files); }}
                      />
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => facturaInputRef.current?.click()}
                        disabled={uploadingFactura || facturas.length >= 6}
                      >
                        {uploadingFactura ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Subiendo...</>
                        ) : (
                          <><i className="bi bi-upload me-1"></i>Subir Factura</>
                        )}
                      </button>
                    </>
                  )}
                </div>

                {facturas.length === 0 ? (
                  <div className="text-center py-3 text-muted border rounded">
                    <i className="bi bi-file-earmark-x fs-3 d-block mb-1"></i>
                    <small>Sin factura adjunta — se requiere para confirmar recepción</small>
                  </div>
                ) : (
                  <div className="row g-2">
                    {facturas.map(f => (
                      <div key={f.id} className="col-md-6">
                        <div className="card border">
                          <div className="card-body py-2 px-3 d-flex align-items-center gap-2">
                            {f.archivo_tipo?.startsWith('image') ? (
                              <img src={getImageUrl(f.archivo_url)} alt={f.archivo_nombre} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }} />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 rounded" style={{ width: 50, height: 50 }}>
                                <i className="bi bi-file-pdf text-danger fs-4"></i>
                              </div>
                            )}
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="small fw-semibold text-truncate">{f.archivo_nombre}</div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {f.archivo_size ? `${(f.archivo_size / 1024).toFixed(0)} KB` : ''}
                              </div>
                            </div>
                            <div className="d-flex gap-1">
                              <a href={getImageUrl(f.archivo_url)} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" title="Ver"><i className="bi bi-eye"></i></a>
                              {showDetalle.estado === 'pendiente' && !modoEspectador && (
                                <button className="btn btn-sm btn-outline-danger" title="Eliminar" onClick={() => handleDeleteFactura(f.id)}><i className="bi bi-trash"></i></button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetalle(null)}>Cerrar</button>
                {showDetalle.estado === 'pendiente' && !modoEspectador && (
                  <>
                    <button type="button" className="btn btn-success" onClick={() => confirmar(showDetalle.id)} disabled={!showDetalle.tiene_factura}>
                      <i className="bi bi-check-lg me-2"></i>Confirmar Recepción
                      {!showDetalle.tiene_factura && <small className="ms-1">(requiere factura)</small>}
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => { cancelar(showDetalle.id); setShowDetalle(null); }}><i className="bi bi-x-lg me-2"></i>Cancelar</button>
                  </>
                )}
                {showDetalle.estado === 'confirmada' && showDetalle.estado_pago !== 'pagado' && !modoEspectador && (
                  <button type="button" className="btn btn-warning" onClick={() => { setShowPagoModal(showDetalle); setPagoForm({ metodo_pago: 'caja_chica', numero_documento: '' }); cargarCajaChica(); }}>
                    <i className="bi bi-cash me-2"></i>Registrar Pago
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL: Registrar Pago                                            */}
      {/* ================================================================ */}
      {showPagoModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowPagoModal(null)} />
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title"><i className="bi bi-cash me-2"></i>Registrar Pago</h5>
                  <button className="btn-close" onClick={() => setShowPagoModal(null)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-info py-2 mb-3">
                    <strong>Compra:</strong> {showPagoModal.numero_compra || '-'}<br />
                    <strong>Proveedor:</strong> {showPagoModal.proveedor_nombre || 'No especificado'}<br />
                    {/* FIX: Safe currency display with parseFloat fallback to prevent $0.00 display bug */}
                    <strong>Total a pagar:</strong> <span className="fw-bold text-danger">{formatCurrency(parseFloat(showPagoModal.total) || 0)}</span>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Método de Pago</label>
                    <select className="form-select" value={pagoForm.metodo_pago} onChange={e => setPagoForm({...pagoForm, metodo_pago: e.target.value})}>
                      <option value="caja_chica">Caja Chica</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="transferencia">Transferencia Bancaria</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </div>

                  {cajaChica && (
                    <div className="alert alert-warning py-2">
                      <i className="bi bi-safe me-1"></i>
                      <strong>Caja Chica:</strong> Disponible {formatCurrency(cajaChica.monto_disponible)}
                      {parseFloat(showPagoModal.total) > parseFloat(cajaChica.monto_disponible) && (
                        <div className="text-danger mt-1"><i className="bi bi-exclamation-triangle me-1"></i>Fondos insuficientes</div>
                      )}
                      <small className="d-block text-muted mt-1">Todos los pagos de compras se descuentan de caja chica. Se genera un gasto que requiere comprobante.</small>
                    </div>
                  )}
                  {!cajaChica && (
                    <div className="alert alert-danger py-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      <strong>No hay caja chica activa.</strong> Debe abrir una caja chica antes de registrar pagos.
                    </div>
                  )}

                  {['transferencia', 'cheque'].includes(pagoForm.metodo_pago) && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Nº Documento / Referencia</label>
                      <input type="text" className="form-control" placeholder="Nº de transferencia, cheque, etc."
                        value={pagoForm.numero_documento} onChange={e => setPagoForm({...pagoForm, numero_documento: e.target.value})} />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowPagoModal(null)} disabled={savingPago}>Cancelar</button>
                  <button className="btn btn-warning" onClick={handleRegistrarPago} disabled={savingPago || !cajaChica || (cajaChica && parseFloat(showPagoModal.total) > parseFloat(cajaChica.monto_disponible))}>
                    {savingPago ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Procesando...</>
                    ) : (
                      <><i className="bi bi-check-lg me-1"></i>Confirmar Pago</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Compras;
// build 1773861338
